import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper } from '@mui/material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import axios from '../../api/axios';

// Try to import katex, but handle gracefully if not installed
let katex = null;
try {
  katex = require('katex');
  require('katex/dist/katex.min.css');
} catch (e) {
  // katex not installed, will fallback to plain text
  console.warn('KaTeX not installed. Math formulas will display as plain text. Install with: npm install katex');
}

// Enhanced Markdown renderer component with math support
const MarkdownRenderer = ({ content }) => {
  if (!content) return null;
  
  // Enhanced fallback: basic markdown rendering with code block, math, and table support
  const lines = content.split('\n');
  const elements = [];
  let inCodeBlock = false;
  let codeBlock = [];
  let codeLanguage = '';
  let currentParagraph = [];
  let inTable = false;
  let tableRows = [];
  // let tableHeaders = []; // Reserved for future table header support
  
  // Render math formula
  const renderMath = (formula, display = false) => {
    if (!katex) {
      // Fallback: show formula as-is if katex is not available
      console.warn('KaTeX not available, showing formula as text:', formula);
      return <span style={{ fontFamily: 'monospace', color: '#666', fontStyle: 'italic' }}>{display ? `$$${formula}$$` : `$${formula}$`}</span>;
    }
    
    // Clean up formula: remove stray $ (causes "Can't use function '$' in math mode") and extra whitespace
    const cleanedFormula = formula.trim().replace(/\$/g, '');
    
    // Check if the rendered HTML contains error indicators
    try {
      const html = katex.renderToString(cleanedFormula, {
        throwOnError: false,
        displayMode: display,
        strict: false
      });
      
      // Check if KaTeX returned an error (contains error class)
      if (html && html.includes('katex-error')) {
        console.warn('KaTeX rendering error for formula:', cleanedFormula, 'HTML:', html);
        // Still render it, but the error will be visible
      }
      
      return <span dangerouslySetInnerHTML={{ __html: html }} />;
    } catch (error) {
      console.error('Math rendering error:', error, 'Formula:', cleanedFormula);
      return <span style={{ color: 'red' }}>{display ? `$$${cleanedFormula}$$` : `$${cleanedFormula}$`}</span>;
    }
  };
  
  // Process text with math formulas
  const processTextWithMath = (text) => {
    const parts = [];
    let lastIndex = 0;
    
    // Process block math first: $$...$$ and \[...\]
    const blockMathRegex = /\$\$([\s\S]*?)\$\$|\\\[([\s\S]*?)\\\]/g;
    let match;
    const blockMatches = [];
    
    while ((match = blockMathRegex.exec(text)) !== null) {
      // match[1] is for $$...$$, match[2] is for \[...\]
      const formula = match[1] || match[2];
      blockMatches.push({
        index: match.index,
        length: match[0].length,
        formula: formula.trim()
      });
    }
    
    // Process inline math: $...$ and \(...\)
    // Match $...$ but exclude $$ (block math)
    // Also match \(...\) for LaTeX inline math
    // Use a more permissive pattern: match any characters (including backslashes) except $ and newline
    // This ensures formulas with LaTeX commands like \int, \, are matched correctly
    const inlineMathRegex = /\$(?![$])([^$\n]+?)\$|\\\(([\s\S]*?)\\\)/g;
    const inlineMatches = [];
    
    // Reset regex lastIndex to avoid issues with global regex
    inlineMathRegex.lastIndex = 0;
    let inlineMatch;
    while ((inlineMatch = inlineMathRegex.exec(text)) !== null) {
      // Capture inlineMatch in closure to avoid unsafe reference
      const currentMatch = inlineMatch;
      // Check if this inline math is inside a block math
      const isInsideBlockMath = blockMatches.some(bm => 
        currentMatch.index >= bm.index && currentMatch.index < bm.index + bm.length
      );
      if (!isInsideBlockMath) {
        // currentMatch[1] is for $...$, currentMatch[2] is for \(...\) (with parentheses)
        const formula = currentMatch[1] || currentMatch[2];
        // For \(...\), remove the outer parentheses that are part of the delimiter
        let cleanFormula = formula;
        if (currentMatch[2]) {
          // currentMatch[2] includes the parentheses, so remove them
          cleanFormula = formula.slice(1, -1);
        }
        inlineMatches.push({
          index: currentMatch.index,
          length: currentMatch[0].length,
          formula: cleanFormula.trim()
        });
      }
    }
    
    // Combine and sort all math matches
    const allMatches = [
      ...blockMatches.map(m => ({ ...m, isBlock: true })),
      ...inlineMatches.map(m => ({ ...m, isBlock: false }))
    ].sort((a, b) => a.index - b.index);
    
    // Process text segments
    allMatches.forEach((mathMatch) => {
      // Add text before math
      if (mathMatch.index > lastIndex) {
        const textBefore = text.substring(lastIndex, mathMatch.index);
        parts.push({ type: 'text', content: textBefore });
      }
      
      // Add math
      parts.push({ type: 'math', formula: mathMatch.formula, display: mathMatch.isBlock });
      
      lastIndex = mathMatch.index + mathMatch.length;
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.substring(lastIndex) });
    }
    
    if (parts.length === 0) {
      parts.push({ type: 'text', content: text });
    }
    
    return parts;
  };
  
  // Render cell content (simple markdown formatting without recursion)
  const renderCellContent = (text) => {
    if (!text) return null;
    
    // Process text with basic markdown formatting
    let processedText = text;
    
    // Inline code: `code` - use theme-aware colors
    processedText = processedText.replace(/`([^`]+)`/g, '<code class="markdown-inline-code">$1</code>');
    
    // Bold: **text**
    processedText = processedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Italic: *text* (but not if it's part of **text**)
    processedText = processedText.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
    
    return <span dangerouslySetInnerHTML={{ __html: processedText }} />;
  };
  
  // Parse and render markdown table
  const renderTable = (tableRows) => {
    if (tableRows.length === 0) return null;
    
    // First row is headers, separator row is second (if exists), data rows start from third
    // But if there's no separator, first row is headers, rest are data
    let headers = [];
    let dataRows = [];
    
    if (tableRows.length > 1 && isTableSeparatorRow(tableRows[1])) {
      // Has separator row
      headers = tableRows[0];
      dataRows = tableRows.slice(2);
    } else {
      // No separator row, first row is headers
      headers = tableRows[0];
      dataRows = tableRows.slice(1);
    }
    
    if (headers.length === 0) return null;
    
    return (
      <Box key={`table-${elements.length}`} sx={{ my: 2, overflowX: 'auto' }}>
        <Paper elevation={1} sx={{ overflow: 'hidden' }}>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow>
                {headers.map((header, idx) => (
                  <TableCell key={idx} sx={{ fontWeight: 600, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'grey.100' }}>
                    {renderCellContent(header.trim())}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dataRows.map((row, rowIdx) => (
                <TableRow key={rowIdx}>
                  {row.map((cell, cellIdx) => (
                    <TableCell key={cellIdx}>
                      {renderCellContent(cell.trim())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    );
  };
  
  // 处理行内 Markdown（加粗、斜体、行内代码）
  const processInlineMarkdown = (text) => {
    if (!text) return '';
    let processed = text
      .replace(/`([^`]+)`/g, '<code class="markdown-inline-code">$1</code>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
    return processed;
  };

  // Check if a line is an ATX-style heading (# to ######)
  const isHeadingLine = (line) => /^#+\s+.+$/.test(line.trim());

  // 标题字号：整体偏小，# 最大，###### 最小
  const headingFontSize = { 1: '1.25rem', 2: '1.1rem', 3: '1rem', 4: '0.95rem', 5: '0.9rem', 6: '0.85rem' };
  const renderHeadingLine = (line) => {
    const trimmed = line.trim();
    const match = trimmed.match(/^(#+)\s+(.+)$/);
    if (!match) return null;
    const level = Math.min(match[1].length, 6);
    const headerText = match[2];
    const headerParts = processTextWithMath(headerText);
    const headerElements = headerParts.map((part, idx) => {
      if (part.type === 'math') {
        return <React.Fragment key={idx}>{renderMath(part.formula, part.display)}</React.Fragment>;
      }
      return <span key={idx} dangerouslySetInnerHTML={{ __html: processInlineMarkdown(part.content) }} />;
    });
    const fontSize = headingFontSize[level] || headingFontSize[6];
    return (
      <Typography
        key={`h-${elements.length}`}
        component={`h${level}`}
        sx={{
          fontSize,
          fontWeight: 700,
          mb: 0.5,
          mt: level <= 2 ? 0 : 1,
          lineHeight: 1.4,
        }}
      >
        {headerElements}
      </Typography>
    );
  };

  // Check if a line is a table row
  const isTableRow = (line) => {
    // Table row: starts and ends with |, contains at least one |
    return line.trim().startsWith('|') && line.trim().endsWith('|') && line.includes('|');
  };
  
  // Check if a line is a table separator (|---|---|)
  const isTableSeparator = (line) => {
    const trimmed = line.trim();
    if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) return false;
    // Check if it contains mostly dashes, colons, and pipes
    const content = trimmed.slice(1, -1);
    return /^[\s|\-:]+$/.test(content);
  };
  
  // Check if a string array represents a separator row
  const isTableSeparatorRow = (cells) => {
    if (cells.length === 0) return false;
    const joined = cells.join('|');
    return isTableSeparator('|' + joined + '|');
  };
  
  // Parse table row into cells
  const parseTableRow = (line) => {
    return line.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
  };
  
  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const text = currentParagraph.join('\n').trim();
      if (text) {
        // Check if the paragraph is a block math: $$...$$ or \[...\]
        const blockMathMatchDollar = text.match(/^\$\$([\s\S]*?)\$\$$/);
        const blockMathMatchBracket = text.match(/^\\\[([\s\S]*?)\\\]$/);
        if (blockMathMatchDollar || blockMathMatchBracket) {
          const formula = blockMathMatchDollar ? blockMathMatchDollar[1] : blockMathMatchBracket[1];
          elements.push(
            <Box key={`math-block-${elements.length}`} sx={{ my: 2, overflowX: 'auto', overflowY: 'hidden' }}>
              {renderMath(formula.trim(), true)}
            </Box>
          );
          currentParagraph = [];
          return;
        }
        
        // Check if it's a header: starts with # and is followed by space, and the whole paragraph is just the header
        const headerMatch = text.match(/^(#+)\s+(.+)$/);
        if (headerMatch && text.split('\n').length === 1) {
          const level = Math.min(headerMatch[1].length, 6);
          const headerText = headerMatch[2];
          const headerParts = processTextWithMath(headerText);
          const headerElements = headerParts.map((part, idx) => {
            if (part.type === 'math') {
              return <React.Fragment key={idx}>{renderMath(part.formula, part.display)}</React.Fragment>;
            }
            return <span key={idx} dangerouslySetInnerHTML={{ __html: processInlineMarkdown(part.content) }} />;
          });
          const fontSize = headingFontSize[level] || headingFontSize[6];
          elements.push(
            <Typography key={`h-${elements.length}`} component={`h${level}`} sx={{ fontSize, fontWeight: 700, mb: 0.5, mt: level <= 2 ? 0 : 1, lineHeight: 1.4 }}>
              {headerElements}
            </Typography>
          );
        } else {
          // Process regular paragraph with math
          const parts = processTextWithMath(text);
          const paragraphElements = parts.map((part, idx) => {
            if (part.type === 'math') {
              if (part.display) {
                // Block math in paragraph (shouldn't happen often, but handle it)
                return (
                  <Box key={idx} component="span" sx={{ display: 'block', my: 1, overflowX: 'auto' }}>
                    {renderMath(part.formula, true)}
                  </Box>
                );
              } else {
                // Inline math
                return <React.Fragment key={idx}>{renderMath(part.formula, false)}</React.Fragment>;
              }
            } else {
              // Regular text - apply markdown formatting
              let processedText = part.content;
              
              // 获取图片请求的 API 源（优先完整 API URL，避免 /api 相对路径时请求到前端域名）
              const getImageOrigin = () => {
                const envApi = typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL;
                if (envApi && (envApi.startsWith('http://') || envApi.startsWith('https://'))) {
                  try { return new URL(envApi).origin; } catch (e) { }
                }
                const baseURL = axios.defaults.baseURL || '';
                if (baseURL && (baseURL.startsWith('http://') || baseURL.startsWith('https://'))) {
                  try { return new URL(baseURL).origin; } catch (e) { }
                }
                return typeof window !== 'undefined' ? window.location.origin : '';
              };
              // Images: ![alt](url) (must be processed before links to avoid conflicts)
              processedText = processedText.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, url) => {
                // Convert relative URLs to absolute URLs if needed
                let imageUrl = url;
                
                // If it's already an absolute URL (http/https/data), use it as-is
                if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
                  imageUrl = url;
                } else if (url.startsWith('/api/')) {
                  imageUrl = getImageOrigin() + url;
                } else if (url.startsWith('/')) {
                  // Other root-relative paths - use as-is (browser will resolve)
                  imageUrl = url;
                } else if (!url.includes('/')) {
                  // 纯文件名（如 2023tt1.png）：走“按原名查找”接口
                  const byNamePath = '/api/upload/image/by-name/' + encodeURIComponent(url);
                  imageUrl = getImageOrigin() + byNamePath;
                } else {
                  const baseURL = axios.defaults.baseURL || '';
                  imageUrl = baseURL ? baseURL.replace(/\/$/, '') + '/' + url : url;
                }
                // 错误提示里显示实际请求的 URL，便于排查（title 悬停可看完整链接）
                const errLabel = (alt || url).replace(/"/g, '&quot;');
                const errTitle = imageUrl.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/&/g, '&amp;');
                return `<img src="${imageUrl}" alt="${(alt || '').replace(/"/g, '&quot;')}" style="max-width: 100%; height: auto; border-radius: 4px; margin: 8px 0; display: block;" onerror="this.style.display='none'; this.nextElementSibling && (this.nextElementSibling.style.display='block');" /><span style="display: none; color: #999; font-size: 0.875rem;" title="请求地址: ${errTitle}">图片加载失败: ${errLabel}</span>`;
              });
              
              // Inline code: `code` (must be processed before bold/italic to avoid conflicts)
              processedText = processedText.replace(/`([^`]+)`/g, '<code class="markdown-inline-code">$1</code>');
              
              // Bold: **text**
              processedText = processedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
              
              // Italic: *text* (but not if it's part of **text**)
              processedText = processedText.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
              
              // Links: [text](url) (but not if it's part of an image)
              processedText = processedText.replace(/(?<!!)\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
              
              return (
                <span key={idx} dangerouslySetInnerHTML={{ __html: processedText }} />
              );
            }
          });
          
          elements.push(
            <Typography 
              key={`p-${elements.length}`} 
              variant="body1" 
              sx={{ 
                mb: 1, 
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                maxWidth: '100%',
                color: 'text.primary',
                backgroundColor: 'transparent',
                '& .katex': {
                  fontSize: '1em',
                  color: 'text.primary',
                },
                '& .katex-display': {
                  margin: '0.5em 0'
                }
              }}
            >
              {paragraphElements}
            </Typography>
          );
        }
      }
      currentParagraph = [];
    }
  };
  
  lines.forEach((line, idx) => {
    if (line.startsWith('```')) {
      // End table if in table
      if (inTable && tableRows.length > 0) {
        elements.push(renderTable(tableRows));
        tableRows = [];
        inTable = false;
      }
      flushParagraph();
      if (inCodeBlock) {
        // End code block
          elements.push(
            <Box key={`code-wrapper-${idx}`} className="markdown-code-block" sx={{ 
              my: 2,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              backgroundColor: '#1e1e1e !important',
            }}>
              <SyntaxHighlighter 
                key={`code-${idx}`} 
                language={codeLanguage || 'text'} 
                style={vscDarkPlus}
                showLineNumbers={codeBlock.length > 3}
                wrapLongLines={false}
                customStyle={{
                  margin: 0,
                  padding: '16px 20px',
                  fontSize: '0.8125rem',
                  lineHeight: 1.7,
                  borderRadius: 0,
                  backgroundColor: 'transparent',
                }}
                codeTagProps={{ style: { fontFamily: 'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' } }}
              >
                {codeBlock.join('\n')}
              </SyntaxHighlighter>
            </Box>
          );
        codeBlock = [];
        codeLanguage = '';
        inCodeBlock = false;
      } else {
        // Start code block
        codeLanguage = line.replace('```', '').trim();
        inCodeBlock = true;
      }
    } else if (inCodeBlock) {
      codeBlock.push(line);
    } else if (isTableSeparator(line)) {
      // Table separator line - skip it but continue table
      if (inTable) {
        // This is the separator row, add it to tableRows so we can detect it later
        const cells = parseTableRow(line);
        if (cells.length > 0) {
          tableRows.push(cells);
        }
      } else {
        // This shouldn't happen, but handle it
        flushParagraph();
      }
    } else if (isTableRow(line)) {
      // Table row
      if (!inTable) {
        // Start new table - flush any pending paragraph
        flushParagraph();
        inTable = true;
        tableRows = [];
      }
      const cells = parseTableRow(line);
      if (cells.length > 0) {
        tableRows.push(cells);
      }
    } else if (line.trim() === '') {
      // Empty line - end table if in table, otherwise flush paragraph
      if (inTable && tableRows.length > 0) {
        elements.push(renderTable(tableRows));
        tableRows = [];
        inTable = false;
      }
      flushParagraph();
    } else if (isHeadingLine(line)) {
      // ATX-style heading: ### 概念 / #### 总结 等，先 flush 再单独渲染
      if (inTable && tableRows.length > 0) {
        elements.push(renderTable(tableRows));
        tableRows = [];
        inTable = false;
      }
      flushParagraph();
      const headingEl = renderHeadingLine(line);
      if (headingEl) elements.push(headingEl);
    } else {
      // Regular line
      if (inTable) {
        // End table and start new paragraph
        if (tableRows.length > 0) {
          elements.push(renderTable(tableRows));
          tableRows = [];
        }
        inTable = false;
      }
      currentParagraph.push(line);
    }
  });
  
  flushParagraph();
  
  // Flush any remaining table
  if (inTable && tableRows.length > 0) {
    elements.push(renderTable(tableRows));
  }
  
  if (inCodeBlock && codeBlock.length > 0) {
    elements.push(
      <Box key="code-wrapper-final" className="markdown-code-block" sx={{ 
        my: 2,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        backgroundColor: '#1e1e1e !important',
      }}>
        <SyntaxHighlighter 
          key="code-final" 
          language={codeLanguage || 'text'} 
          style={vscDarkPlus}
          showLineNumbers={codeBlock.length > 3}
          wrapLongLines={false}
          customStyle={{
            margin: 0,
            padding: '16px 20px',
            fontSize: '0.8125rem',
            lineHeight: 1.7,
            borderRadius: 0,
            backgroundColor: 'transparent',
          }}
          codeTagProps={{ style: { fontFamily: 'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' } }}
        >
          {codeBlock.join('\n')}
        </SyntaxHighlighter>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      width: '100%',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      color: 'text.primary',
      backgroundColor: 'transparent',
      '& *': {
        maxWidth: '100%',
        color: 'inherit',
        backgroundColor: 'transparent',
      },
      '& p': {
        color: 'text.primary',
        backgroundColor: 'transparent',
      },
      '& strong': {
        color: 'text.primary',
        backgroundColor: 'transparent',
      },
      '& em': {
        color: 'text.primary',
        backgroundColor: 'transparent',
      },
      '& span': {
        color: 'text.primary',
        backgroundColor: 'transparent',
      },
      '& div': {
        backgroundColor: 'transparent',
        color: 'text.primary',
      },
      '& .markdown-inline-code': {
        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5',
        padding: '2px 6px',
        borderRadius: '3px',
        fontFamily: 'monospace',
        fontSize: '0.9em',
        color: (theme) => theme.palette.mode === 'dark' ? '#ff6b6b' : '#d73a49',
      },
      '& a': {
        color: 'primary.main',
        textDecoration: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
      '& img': {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: '4px',
        margin: '8px 0',
        display: 'block',
      },
      '& .katex': {
        fontSize: '1em',
        color: 'text.primary',
        backgroundColor: 'transparent',
      },
      '& .katex-display': {
        margin: '0.5em 0',
        overflowX: 'auto',
        overflowY: 'hidden'
      },
      '& .markdown-code-block': {
        backgroundColor: '#1e1e1e !important',
        '& pre, & pre *': { backgroundColor: 'transparent !important' },
      }
    }}>
      {elements}
    </Box>
  );
};

export default MarkdownRenderer;
